// src/pages/PrintBillPage.js
// หน้าแสดงใบเสร็จ / บิล สำหรับพิมพ์
// รองรับ: ส่วนลด, ผู้ทำรายการ, แก้ราคา/หน่วย, default_paper_size จาก Settings
// และเลือกขนาดกระดาษ 58 / 80 / A4 พร้อมพรีวิวบนจอให้ถูกต้อง

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { useNotification } from "../NotificationContext";

const PrintBillPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const [paperSize, setPaperSize] = useState("80");
  const [fontSize, setFontSize] = useState("0.8rem");
  const [colorMode, setColorMode] = useState("bw"); // Default to BW for thermal printing

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [orderRes, settingsRes] = await Promise.all([
          api.get(`/orders/${orderId}`),
          api.get("/settings"),
        ]);
        const cfg = settingsRes.data || {};
        setOrder(orderRes.data);
        setSettings(cfg);
        if (cfg.default_paper_size) {
          setPaperSize(cfg.default_paper_size);
        }
        // Initialize font size
        if (cfg.default_bill_font_size) {
          setFontSize(cfg.default_bill_font_size);
        } else {
          // Fallback based on paper size
          if (cfg.default_paper_size === "58") setFontSize("0.78rem");
          else if (cfg.default_paper_size === "80") setFontSize("0.8rem");
          else setFontSize("0.9rem");
        }
      } catch (err) {
        addNotification(
          err.response?.data?.message || "โหลดข้อมูลใบเสร็จไม่สำเร็จ",
          "danger"
        );
      } finally {
        setLoading(false);
      }
    };
    if (orderId) loadData();
  }, [orderId, addNotification]);

  const handlePrint = () => window.print();

  const adjustFontSize = (delta) => {
    setFontSize((prev) => {
      const val = parseFloat(prev);
      const newVal = Math.max(0.5, Math.min(2.0, val + delta)); // Limit between 0.5 and 2.0 rem
      return newVal.toFixed(2) + "rem";
    });
  };

  const formatMoney = (v) =>
    (Number(v) || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ราคา/หน่วย: ถ้ามี unit_price (แก้จาก POS) ให้ใช้เลย ไม่งั้น = (amount + line_discount) / qty
  const getUnitPrice = (item) => {
    const qty = Number(item.qty) || 1;
    if (item.unit_price != null) {
      const u = Number(item.unit_price);
      if (!Number.isNaN(u)) return u;
    }
    const amt = Number(item.amount) || 0;
    const disc = Number(item.line_discount) || 0;
    const gross = amt + disc;
    return qty > 0 ? gross / qty : gross;
  };

  const totals = (() => {
    if (!order?.items) return { gross: 0, disc: 0, net: 0 };
    let g = 0,
      d = 0;
    order.items.forEach((i) => {
      const a = Number(i.amount) || 0;
      const l = Number(i.line_discount) || 0;
      g += a + l;
      d += l;
    });
    const net = order.total_amount != null ? Number(order.total_amount) : g - d;
    return { gross: g, disc: d, net };
  })();

  // ความกว้างบน "หน้าจอ" ของ slip / A4
  const stylePaper = (() => {
    let widthPx;
    if (paperSize === "58") {
      widthPx = 260; // ประมาณ 58mm บนจอ
    } else if (paperSize === "80") {
      widthPx = 340; // ประมาณ 80mm
    } else {
      // A4
      widthPx = 800;
    }
    return {
      width: widthPx,
      maxWidth: "100%",
      fontSize: fontSize, // Use the state variable

      background: "#fff",
      borderRadius: "1rem",
      filter: colorMode === 'bw' ? 'grayscale(100%)' : 'none',
    };
  })();

  // ซ่อนทุกอย่างตอน print ให้เหลือเฉพาะใบเสร็จ + ขนาดกระดาษตามที่เลือก
  const printStyles = `
    @media print {
      /* ซ่อนทุก element ยกเว้น .bill-paper */
      body * {
        visibility: hidden !important;
      }
      .bill-paper, .bill-paper * {
        visibility: visible !important;
      }
      .bill-paper {
        position: absolute;
        left: 0;
        right: 0;
        margin: 0 auto;
        box-shadow: none !important;
        border: none !important;
      }

      /* ซ่อน header / navbar / footer เวอร์ชัน / ฯลฯ */
      .no-print,
      header,
      footer,
      nav,
      .app-footer,
      .app-version,
      .navbar,
      .sidebar {
        display: none !important;
      }

      ${paperSize === "58"
      ? `@page { size: 58mm auto; margin: 3mm; } .bill-paper { width: 58mm; font-size: ${fontSize} !important; }`
      : paperSize === "80"
        ? `@page { size: 80mm auto; margin: 3mm; } .bill-paper { width: 80mm; font-size: ${fontSize} !important; }`
        : `@page { size: A4 portrait; margin: 10mm; } .bill-paper { width: 100%; max-width: 210mm; font-size: ${fontSize} !important; }`
    }
    }
    .bill-paper { filter: ${colorMode === 'bw' ? 'grayscale(100%)' : 'none'} !important; }
    ${colorMode === 'bw' ? `
      .bill-paper, .bill-paper * { color: #000000 !important; border-color: #000000 !important; }
      .bill-paper .badge { background-color: #ffffff !important; border: 1px solid #000000 !important; }
    ` : ''}
    }
  `;

  return (
    <div className="dashboard-page container py-3">
      <style>{printStyles}</style>

      {/* แถบด้านบน (ไม่ถูกพิมพ์) */}
      <div className="no-print d-flex justify-content-between align-items-center mb-3 pos-hero">
        <div>
          <div className="dashboard-hero-title mb-0">
            พิมพ์ใบเสร็จ / Receipt
          </div>
          <div className="dashboard-hero-subtitle">
            เลือกขนาดกระดาษให้ตรงกับเครื่องพิมพ์ของคุณ
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={paperSize}
            onChange={(e) => setPaperSize(e.target.value)}
          >
            <option value="58">สลิป 58 มม.</option>
            <option value="80">สลิป 80 มม.</option>
            <option value="a4">A4</option>
          </select>
          {/* Font Size Control: Buttons - / + */}
          <div className="btn-group btn-group-sm bg-white">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => adjustFontSize(-0.05)}
              title="ลดขนาดตัวอักษร"
            >
              <i className="fas fa-minus"></i>
            </button>
            <span className="btn btn-outline-secondary disabled text-dark" style={{ minWidth: "60px", opacity: 1 }}>
              {fontSize}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => adjustFontSize(0.05)}
              title="เพิ่มขนาดตัวอักษร"
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>

          <div className="btn-group btn-group-sm">
            <button
              type="button"
              className={`btn ${colorMode === 'bw' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setColorMode('bw')}
            >
              ดำ
            </button>
            <button
              type="button"
              className={`btn ${colorMode === 'color' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setColorMode('color')}
            >
              สี
            </button>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            style={{ backgroundColor: "#fff", color: "#d4af37", border: "1px solid #d4af37" }}
            onClick={() => navigate(-1)}
          >
            ← กลับ
          </button>
          <button
            type="button"
            className="btn btn-ks-success btn-sm"
            onClick={handlePrint}
          >
            <i className="fas fa-print me-1"></i>
            พิมพ์
          </button>
        </div>
      </div>

      {loading && <div>กำลังโหลด...</div>}

      {!loading && order && (
        <div className="d-flex justify-content-center">
          <div
            className="bill-paper card-ks-soft p-3 shadow-soft"
            style={stylePaper}
          >
            {/* หัวบิล */}
            <div
              className="text-center mb-2"
              style={{ whiteSpace: "pre-line" }}
            >
              {settings.bill_logo_url && (
                <img
                  src={settings.bill_logo_url}
                  alt="logo"
                  style={{
                    maxHeight: 60,
                    maxWidth: "100%",
                    objectFit: "contain",
                    marginBottom: 8,
                  }}
                />
              )}
              <div className="fw-bold" style={{ color: "#d4af37" }}>
                {settings.shop_name || "POS88"}
              </div>
              {settings.shop_address && (
                <div className="small">
                  {settings.shop_address}
                </div>
              )}
              {settings.shop_phone && (
                <div className="small">
                  โทร {settings.shop_phone}
                </div>
              )}
              {settings.bill_header_text && (
                <div
                  style={{
                    fontSize: "0.9em",
                    color: "#555",
                    marginTop: 4,
                  }}
                >
                  {settings.bill_header_text}
                </div>
              )}
            </div>

            <hr className="my-2" />

            {/* ข้อมูลบิล */}
            <div className="small mb-2">
              <div className="d-flex justify-content-between">
                <span>เลขบิล</span>
                <span>{order.order_code}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>วันที่</span>
                <span>
                  {new Date(
                    order.server_time || order.created_at
                  ).toLocaleString("th-TH")}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span>ลูกค้า</span>
                <span>{order.customer_name || "-"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>โทร</span>
                <span>{order.customer_phone || "-"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>ผู้ทำรายการ</span>
                <span>
                  {order.created_by_name ||
                    order.created_by_username ||
                    order.created_by ||
                    "-"}
                </span>
              </div>
            </div>

            <hr className="my-2" />

            {/* รายการ */}
            <table className="w-100">
              <thead>
                <tr>
                  <th className="text-start border-bottom">บริการ</th>
                  <th className="text-end border-bottom">จำนวน</th>
                  <th className="text-end border-bottom">ยอด</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it) => {
                  const disc = Number(it.line_discount) || 0;
                  const u = getUnitPrice(it);
                  return (
                    <tr key={it.trans_id}>
                      <td className="py-1">
                        {it.service_name}
                        {it.main_emp_name && (
                          <div className="text-muted small">
                            ผู้ให้บริการ {it.main_emp_name}
                          </div>
                        )}
                        {disc > 0 && (
                          <div className="text-danger small">
                            ลด {formatMoney(disc)} ฿
                          </div>
                        )}
                      </td>
                      <td className="text-end align-top py-1">{it.qty}</td>
                      <td className="text-end align-top py-1">
                        {formatMoney(it.amount)} ฿
                        <div className="text-muted small">
                          ({formatMoney(u)} / หน่วย)
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <hr className="my-2" />

            {/* ยอดรวม */}
            <div className="small">
              <div className="d-flex justify-content-between">
                <span>ยอดก่อนส่วนลด</span>
                <span>{formatMoney(totals.gross)} ฿</span>
              </div>

              {/* Breakdown: Line Discount */}
              {totals.disc > 0 && (
                <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.9em' }}>
                  <span>ส่วนลดรายการ</span>
                  <span>- {formatMoney(totals.disc)} ฿</span>
                </div>
              )}

              {/* Breakdown: Code Discount */}
              {order.discount_code && (
                <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.9em' }}>
                  <span>โค้ดส่วนลด ({order.discount_code})</span>
                  <span>
                    - {formatMoney((order.discount_amount || 0) - totals.disc)} ฿
                  </span>
                </div>
              )}

              {/* Total Discount */}
              <div className="d-flex justify-content-between text-danger">
                <span>ส่วนลดรวม</span>
                <span>- {formatMoney(order.discount_amount || 0)} ฿</span>
              </div>
              <div className="d-flex justify-content-between mt-1 fw-bold" style={{ color: "#d4af37" }}>
                <span>ยอดสุทธิ</span>
                <span>{formatMoney(totals.net)} ฿</span>
              </div>
              {order.payment_method && (
                <div className="mt-1">
                  <div className="d-flex justify-content-between">
                    <span>วิธีชำระ</span>
                    <span>
                      {order.payment_method === "split"
                        ? "เงินสด + โอน"
                        : order.payment_method === "cash"
                          ? "เงินสด"
                          : "โอน"}
                    </span>
                  </div>
                  {(order.payment_method === "cash" || order.payment_method === "split") && (
                    <div className="d-flex justify-content-between">
                      <span>รับเงินสด</span>
                      <span>{formatMoney(order.cash_amount || 0)} ฿</span>
                    </div>
                  )}
                  {/* Deposit Usage */}
                  {order.deposit_usages && order.deposit_usages.length > 0 && (
                    <div className="d-flex justify-content-between">
                      <span>หักเงินมัดจำ</span>
                      <span>
                        {formatMoney(order.deposit_usages.reduce((s, u) => s + (Number(u.amount_used) || 0), 0))} ฿
                      </span>
                    </div>
                  )}
                  {(order.payment_method === "transfer" || order.payment_method === "split") && (
                    <div className="d-flex justify-content-between">
                      <span>รับโอน</span>
                      <span>{formatMoney(order.transfer_amount || 0)} ฿</span>
                    </div>
                  )}
                  {Number(order.change_amount) > 0 && (
                    <div className="d-flex justify-content-between text-success">
                      <span>เงินทอน</span>
                      <span>{formatMoney(order.change_amount)} ฿</span>
                    </div>
                  )}
                  {order.payment_note && (
                    <div className="mt-1 text-muted">{order.payment_note}</div>
                  )}
                </div>
              )}
            </div>

            {order.external_note && (
              <div className="mt-2 small">
                <div className="fw-semibold">หมายเหตุ:</div>
                <div style={{ whiteSpace: "pre-line" }}>
                  {order.external_note}
                </div>
              </div>
            )}

            {/* ลายเซ็นผู้รับเงิน */}
            <div className="mt-3" style={{ paddingTop: 8 }}>
              <div className="d-flex justify-content-end" style={{ marginTop: 24 }}>
                <div style={{ width: 220, textAlign: "center" }}>
                  <div style={{ borderBottom: "1px dotted #999", height: 2, marginBottom: 6 }}></div>
                  <div className="small">ลงชื่อผู้รับเงิน</div>
                  <div className="small text-muted">
                    {order.created_by_name || order.created_by_username || ""}
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-2" />

            {/* ท้ายบิล */}
            <div
              className="text-center small"
              style={{ whiteSpace: "pre-line" }}
            >
              {settings.bill_footer_promo && (
                <div className="mb-1">{settings.bill_footer_promo}</div>
              )}
              {settings.bill_qr_image_url && (
                <div className="mb-1">
                  <img
                    src={settings.bill_qr_image_url}
                    alt="qr"
                    style={{
                      maxHeight: 100,
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
              <div className="mt-1">ขอบคุณที่ใช้บริการ 💖</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintBillPage;
